import { useEffect, useMemo, useState } from "react";

import { Tab } from "~/shared/types/thema";
import { ThemaCodeType } from "reedy-data/api";
import { useThemaCodesQuery } from "./use-thema-codes-query";
import { useNavigationState } from "./use-navigation-state";
import { useInfiniteScroll } from "./use-infinite-scroll";
import { useThemaSearch } from "./use-thema-search";

interface UseThemaCodesParams {
  query: string;
  activeTab: Tab;
  themaLanguage?: string;
  pageSize?: number;
}

export const useThemaCodes = ({
  query,
  activeTab,
  themaLanguage = "en",
  pageSize = 50,
}: UseThemaCodesParams) => {
  const [searchPage, setSearchPage] = useState(1);

  const {
    navigationStack,
    currentParent,
    handleDrillDown,
    handleNavigateToLevel,
  } = useNavigationState(activeTab, query);

  useEffect(() => {
    // resets pagination to the first page when the query or tab changes; searchPage feeds the search request, so resetting during render would alter request timing
    setSearchPage(1);
  }, [query, activeTab]);

  const codeType =
    activeTab === Tab.SUBJECTS
      ? ThemaCodeType.SUBJECT
      : ThemaCodeType.QUALIFIER;

  const searchOptions = (type: ThemaCodeType) => ({
    searchParams: query.trim()
      ? {
          q: query,
          type,
          page: searchPage,
          per_page: pageSize,
          language: themaLanguage,
        }
      : undefined,
    enabled: !!query.trim(),
    keepPreviousData: false,
  });

  // Both queries are necessary in order to show the count of the thema codes based
  // on the search
  const queries = {
    [ThemaCodeType.SUBJECT]: useThemaSearch(
      searchOptions(ThemaCodeType.SUBJECT),
    ),
    [ThemaCodeType.QUALIFIER]: useThemaSearch(
      searchOptions(ThemaCodeType.QUALIFIER),
    ),
  };

  const searchQuery = queries[codeType];

  const codesQuery = useThemaCodesQuery(
    codeType,
    themaLanguage,
    currentParent?.code,
    !query.trim(),
  );

  const { allSearchResults, hasMorePages, isLoadingMore, scrollContainerRef } =
    useInfiniteScroll({
      searchQuery,
      query,
      activeTab,
      pageSize,
      onPageChange: setSearchPage,
    });

  const { data, isLoading, error } = useMemo(() => {
    if (query.trim()) {
      return {
        data: allSearchResults,
        isLoading: searchQuery.isLoading,
        error: searchQuery.error,
      };
    }

    return {
      data: codesQuery.data?.codes || [],
      isLoading: codesQuery.isLoading,
      error: codesQuery.error,
    };
  }, [
    query,
    allSearchResults,
    searchQuery.isLoading,
    searchQuery.error,
    codesQuery.data,
    codesQuery.isLoading,
    codesQuery.error,
  ]);

  return {
    data,
    isLoading,
    error,
    navigationStack,
    handleDrillDown,
    handleNavigateToLevel,
    scrollContainerRef,
    count: {
      [ThemaCodeType.SUBJECT]: queries[ThemaCodeType.SUBJECT].data?.total,
      [ThemaCodeType.QUALIFIER]: queries[ThemaCodeType.QUALIFIER].data?.total,
    },
    searchQuery: {
      data: searchQuery.data,
      isLoading: searchQuery.isLoading,
    },
    allSearchResults,
    hasMorePages,
    isLoadingMore,
  };
};
